import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import LoadingSpinner from '../components/LoadingSpinner';
import { Users, Car, Calendar, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import { adminAPI } from '../api';

import AdminAnalytics from '../components/admin/AdminAnalytics';
import AdminUsers from '../components/admin/AdminUsers';
import AdminMobilitiers from '../components/admin/AdminMobilitiers';
import AdminPayouts from '../components/admin/AdminPayouts';
import AdminBookings from '../components/admin/AdminBookings';
import AdminCars from '../components/admin/AdminCars';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) return;
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const res = await adminAPI.getStats();
        setStats(res.data);
      } catch (e) {
        console.error('stats error', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Panel de control y gestión de la plataforma</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-users-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Usuarios Totales</CardTitle>
              <Users className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Renters registrados</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-mobilitiers-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mobilitiers</CardTitle>
              <Car className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_mobilitiers || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Propietarios activos</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-cars-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Autos</CardTitle>
              <Calendar className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_cars || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Vehículos publicados</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-bookings-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reservas</CardTitle>
              <TrendingUp className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_bookings || 0}</div>
              <p className="text-xs text-gray-600 mt-1">
                <span className="text-green-600 font-medium">{stats?.active_bookings || 0}</span> activas
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Card */}
        <Card className="mb-8" data-testid="stat-revenue-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" /> Ingresos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600">Ingresos Totales</p>
                <p className="text-3xl font-bold text-gray-900">
                  ${(stats?.total_revenue || 0).toLocaleString()} MXN
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Comisiones (20%)</p>
                <p className="text-2xl font-bold text-green-600">
                  ${(stats?.platform_fees || 0).toLocaleString()} MXN
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ganancias Mobilitiers (80%)</p>
                <p className="text-2xl font-bold text-blue-600">
                  ${((stats?.total_revenue || 0) - (stats?.platform_fees || 0)).toLocaleString()} MXN
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for management */}
        <Tabs defaultValue="analytics" className="space-y-4">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-1" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>
            <TabsTrigger value="mobilitiers" data-testid="tab-mobilitiers">Mobilitiers</TabsTrigger>
            <TabsTrigger value="cars" data-testid="tab-cars">Autos</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Reservas</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">
              <DollarSign className="w-4 h-4 mr-1" /> Pagos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics"><AdminAnalytics /></TabsContent>
          <TabsContent value="users"><AdminUsers /></TabsContent>
          <TabsContent value="mobilitiers"><AdminMobilitiers /></TabsContent>
          <TabsContent value="cars"><AdminCars /></TabsContent>
          <TabsContent value="bookings"><AdminBookings /></TabsContent>
          <TabsContent value="payouts"><AdminPayouts /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
